import React, {
  ChangeEvent,
  forwardRef,
  TextareaHTMLAttributes,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  StyledLabel,
  StyledFieldContainer,
  StyledTextArea,
  StyledTextAreaContainer,
  StyledLabelContainer,
  StyledTextCounter,
} from "./TextArea.styled";
import { CaptionsTyp } from "../../Typography/Typography";
import { theme } from "../../../constants/StyledComponents";
import { FontWeightEnum } from "../../../types/AppTypes";
import { useMergedRefs } from "../../../hooks/useMergedRefs.hook";
import { trimSpaces } from "../../../utils/string.utils";

export interface TextAreaProps extends Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "onChange"
> {
  label?: string;
  error?: string;
  maxLength?: number;
  autoTrim?: boolean;
  onChange?: (value: string) => void;
}

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      name,
      disabled,
      label,
      error,
      value,
      onChange,
      placeholder,
      readOnly,
      maxLength,
      autoTrim = true,
      onBlur: propsOnBlur,
      ...props
    },
    ref,
  ) => {
    const [isActive, setIsActive] = useState<boolean>(!!(value || placeholder));
    const [countChar, setCountChar] = useState((value as string)?.length || 0);
    const isLimitedReached = maxLength != undefined && countChar >= maxLength;
    const [isFocus, setIsFocus] = useState(false);

    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = useMergedRefs(internalRef, ref);

    const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
      const text = e.target.value;

      if (text !== "" || placeholder) {
        setIsActive(true);
      } else {
        setIsActive(false);
      }

      if (onChange) {
        onChange(e.target.value);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      setIsFocus(false);
      if (autoTrim && onChange && internalRef.current) {
        const trimmedValue = trimSpaces(e.target.value);
        if (trimmedValue !== e.target.value) {
          onChange(trimmedValue);
        }
      }
      if (propsOnBlur) {
        propsOnBlur(e);
      }
    };

    useEffect(() => {
      if (value) {
        setIsActive(true);
      }

      setCountChar((value as string)?.length || 0);
    }, [value]);

    return (
      <StyledFieldContainer
        $hasError={!!error}
        onFocus={() => {
          setIsFocus(true);
        }}
      >
        <StyledTextAreaContainer
          disabled={disabled}
          $readOnly={readOnly}
          $hasError={!!error}
        >
          <StyledLabelContainer>
            <StyledLabel $isActive={isActive} $hasError={!!error}>
              {label}
            </StyledLabel>
          </StyledLabelContainer>
          <StyledTextArea
            ref={mergedRef}
            name={name}
            onChange={handleTextChange}
            onBlur={handleBlur}
            disabled={disabled}
            value={value}
            placeholder={placeholder}
            readOnly={readOnly}
            $hasLabel={!!label}
            maxLength={maxLength}
            {...props}
          />
          <StyledTextCounter>
            <CaptionsTyp
              color={
                isLimitedReached && isFocus
                  ? theme.colors.purple
                  : theme.colors.black
              }
              fontWeight={
                isFocus && isLimitedReached
                  ? FontWeightEnum.SEMIBOLD
                  : FontWeightEnum.MEDIUM
              }
            >
              {maxLength && `${countChar}/${maxLength}`}
            </CaptionsTyp>
          </StyledTextCounter>
        </StyledTextAreaContainer>
      </StyledFieldContainer>
    );
  },
);

export default TextArea;
